import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useMemo, useRef } from "react";
import { Box, Tooltip, IconButton, Stack, Button, Typography } from "@mui/material";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { QR } from "../../../qrCode/components/qrUi";
import { NameCell, ChipList, PersonChip, MutedText, StatusPill, filterBoxSx, statusColor } from "../leadUi";
import EditIcon from "@mui/icons-material/Edit";
import DataTableWithCheckbox from "../../../../components/dataTableWithSearch/DataTableWithCheckBox";
import FilterMultiSelect from "../../../../components/selections/filterMultiSelect";
import {
    getMyTaskThunk,
    getCallStatusTypesThunk,
    getLeadStatusTypesThunk,
} from "../../leadMangementThunk";
import Tabs from '../tabs';
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import TaskUpdateModal from "./taskUpdateModal";
import { formatDate } from "../../../../components/formatDateAndTime/formatDateAndTime";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LeadWaChatConvo from "./leadWaChatConvo";
import { setOpenLeadChatConvo } from '../../leadMangementSlice';
import { setSelectUser, setLeadWaDetails } from "../../../messages/messagesSlice";
import { getIndividualUserThunk } from "../../../contact/contactThunk";
import ContactProfileTabModal from "../../../contactProfile/contactProfileTabModal";
import { getTemplateThunk } from '../../../templates/templatesThunk';


export default function MyTask() {
    const dispatch = useDispatch();
    const scrollRef = useRef(null);
    const { getMyTaskDetails = [], status, error, openLeadChatConvo } = useSelector(
        (state) => state.leadManagement
    );

    const [state, setState] = useState({
        activeIndex: 0,
        selectedBranchDept: [],
        selectedTags: [],
        selectedRowData: [],
        openUpdateModal: false,
        openContactEditModal: false,
    });

    const { activeIndex, selectedBranchDept, selectedTags, openUpdateModal, selectedRowData } = state;

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // 👉 Fetch data
    useEffect(() => {
        dispatch(getMyTaskThunk());
        dispatch(getLeadStatusTypesThunk());
        dispatch(getCallStatusTypesThunk());
        dispatch(getTemplateThunk());
    }, [dispatch]);



    // 👉 Columns (memoized)
    const columns = useMemo(
        () => [
            { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
            {
                accessorKey: "name", header: "Name", Cell: ({ row }) => (
                    <NameCell
                        name={row.original.name}
                        title="View contact profile"
                        onClick={() => { setState(prev => ({ ...prev, openContactEditModal: true, selectedRowData: row?.original })); handleClick(row.original?.wa_contact_id); }}
                    />
                )
            },
            // { accessorKey: "email", header: "Email" },
            { accessorKey: "employee_name", header: "Employee Name", Cell: ({ row }) => <PersonChip name={row.original.employee_name} /> },
            { accessorKey: "lead_status_type_name", header: "Status", Cell: ({ row }) => <StatusPill value={row.original.lead_status_type_name} /> },
            { accessorKey: "comments", header: "Comments", Cell: ({ row }) => <MutedText value={row.original.comments} /> },
            { accessorKey: "branch_names", header: "Branch", Cell: ({ row }) => <MutedText value={row.original.branch_names} /> },
            { accessorKey: "department_names", header: "Department", Cell: ({ row }) => <ChipList value={row.original.department_names} color={QR.blue} /> },
            { accessorKey: "tags", header: "Tag", Cell: ({ row }) => <ChipList value={row.original.tags} /> },
            {
                accessorKey: "followup_time", header: "Next FollowUp Date", Cell: ({ row }) => row.original.followup_time ? (
                    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ color: "#475569" }}>
                        <EventOutlinedIcon sx={{ fontSize: 15, color: QR.amber }} />
                        <Typography variant="body2" noWrap>{formatDate(row.original.followup_time)}</Typography>
                    </Stack>
                ) : <Typography variant="body2" color="#cbd5e1">-</Typography>
            }
        ], []
    );

    // 👉 Build unique option list
    const buildOptions = (arr, idKey, nameKey) => {
        const map = new Map();
        let hasUnassigned = false;

        arr.forEach(item => {
            let ids = item[idKey];
            let names = item[nameKey];

            // Handle null / empty → mark unassigned
            if (!ids) {
                hasUnassigned = true;
                return; // skip, add later
            }

            const idList = ids.split(",").map(x => x.trim());
            const nameList = (names || "").split(",").map(x => x.trim());

            idList.forEach((id, idx) => {
                if (id && !map.has(id)) {
                    map.set(id, {
                        value: id,
                        label: nameList[idx] || id
                    });
                }
            });
        });

        // Final list
        const options = Array.from(map.values());

        // Add Un Assign as 1st item only once
        if (hasUnassigned) {
            options.unshift({
                value: "0",
                label: "Un Assign"
            });
        }
        return options;
    };

    const leads = getMyTaskDetails[activeIndex]?.lead_details ?? [];
    const branchDeptOptions = useMemo(() => buildOptions(leads, "branch_department_id", "department_names"), [leads]);
    const tagOptions = useMemo(() => buildOptions(leads, "tag_id", "tags"), [leads]);

    // 👉 Set defaults when leads change
    useEffect(() => {
        const leads = getMyTaskDetails[activeIndex]?.lead_details ?? [];
        const branches = buildOptions(leads, "branch_department_id", "department_names");
        const tags = buildOptions(leads, "tag_id", "tags");

        setState(prev => ({
            ...prev,
            selectedBranchDept: branches,
            selectedTags: tags,
        }));

    }, [getMyTaskDetails, activeIndex]);


    // 👉 Filter data
    const filteredLeads = useMemo(() => {
        if (!leads.length) return [];

        const branchIds = selectedBranchDept.map(b => String(b.value));
        const tagIds = selectedTags.map(t => String(t.value));

        // If no filters applied, return all leads
        if (branchIds.length === 0 && tagIds.length === 0) {
            return [];
        }

        return leads.filter(lead => {
            const leadBranches = lead.branch_department_id
                ? lead.branch_department_id.split(",").map(x => x.trim())
                : ["0"]; // null → 0

            const leadTags = lead.tag_id
                ? lead.tag_id.split(",").map(x => x.trim())
                : ["0"]; // null → 0

            const branchMatch =
                branchIds.length === 0 ||
                leadBranches.some(id => branchIds.includes(id));

            const tagMatch =
                tagIds.length === 0 ||
                leadTags.some(id => tagIds.includes(id));

            return branchMatch && tagMatch;
        });
    }, [leads, selectedBranchDept, selectedTags]);


    // 👉 Row actions
    const renderRowActions = ({ row }) => {
        const { wa_contact_id, name, branch_department_id, department_names, tags, branch_names, tag_id, lead_status_type_id } = row.original;

        return (
            <Box sx={{ display: "flex", gap: 0.75, justifyContent: "center" }}>

                <Tooltip title={`Chat With ${row.original.name}`} disableInteractive>
                    <span>
                        <IconButton
                            sx={actionBtnSx("#16a34a")}
                            onClick={() => {
                                dispatch(setOpenLeadChatConvo(true));
                                dispatch(setSelectUser(wa_contact_id));
                                dispatch(setLeadWaDetails([{
                                    "wa_contact_id": wa_contact_id,
                                    "name": name,
                                    "mobile": "919347081115",
                                    "tag_id": tag_id,
                                    "branch_department_id": branch_department_id,
                                    "last_message_time": "1760181607",
                                    "unread": "0",
                                    "lead_status_type_id": lead_status_type_id,
                                    "last_message": "স্বাগতম!",
                                    "department_name": department_names,
                                    "branch_name": branch_names,
                                    "tag_names": tags
                                }]));
                            }}
                        >
                            <WhatsAppIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title="Update task">
                    <IconButton
                        sx={actionBtnSx(QR.blue)}
                        onClick={() =>
                            setState(prev => ({
                                ...prev,
                                openUpdateModal: true,
                                selectedRowData: row.original
                            }))
                        }
                    >
                        <EditIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        )
    };

    // 👉 Filters UI
    const renderFilters = () => (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2 }}>
            <Box sx={filterBoxSx}>
                <FilterMultiSelect
                    name="branchDept"
                    label="Branch Dept"
                    options={branchDeptOptions}
                    value={selectedBranchDept}
                    onChange={(_, value) =>
                        setState(prev => ({
                            ...prev,
                            selectedBranchDept: value
                        }))
                    }
                />
            </Box>
            <Box sx={filterBoxSx}>
                <FilterMultiSelect
                    name="tags"
                    label="Tag"
                    options={tagOptions}
                    value={selectedTags}
                    onChange={(_, value) =>
                        setState(prev => ({
                            ...prev,
                            selectedTags: value
                        }))
                    }
                />
            </Box>
        </Box>
    );

    // 👉 Error snackbar (optional)
    useEffect(() => {
        if (status === "failed")
            setSnackbar({ open: true, message: error, severity: "error" });
    }, [status, error]);


    // Status pill: coloured dot per status; active = tinted with its own colour
    const getButtonStyles = (index, c) => ({
        height: 30,
        minWidth: 0,
        pl: 1,
        pr: 1.1,
        gap: 0.75,
        borderRadius: 2,
        textTransform: "none",
        fontSize: 12.5,
        fontWeight: activeIndex === index ? 700 : 600,
        whiteSpace: "nowrap",
        color: activeIndex === index ? c : "#475569",
        bgcolor: activeIndex === index ? `${c}12` : "#fff",
        border: `1px solid ${activeIndex === index ? `${c}66` : "#e2e8f0"}`,
        boxShadow: activeIndex === index ? `inset 0 -2px 0 ${c}` : "0 1px 2px rgba(16,24,40,0.04)",
        transition: "all .15s ease",
        "&:hover": {
            bgcolor: activeIndex === index ? `${c}12` : "#f8fafc",
            borderColor: activeIndex === index ? `${c}66` : "#cbd5e1",
        },
    });

    // Round row-action button
    const actionBtnSx = (c) => ({
        width: 32,
        height: 32,
        borderRadius: 2,
        color: c,
        bgcolor: `${c}12`,
        border: `1px solid ${c}26`,
        "&:hover": { color: "#fff", bgcolor: c, borderColor: c },
    });


    const handleClick = (wa_contact_id) => {
        dispatch(getIndividualUserThunk({ wa_contact_id: wa_contact_id }));
    };

    return (
        <Box>
            <Tabs>
                {/* Status pills with counts */}
                <Box sx={{ mt: 1.5, mb: 0.5 }}>
                    <Typography sx={{ mb: 1, fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b" }}>
                        Lead status
                    </Typography>
                    <Box
                        ref={scrollRef}
                        sx={{
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            scrollBehavior: "smooth",
                            width: "100%",
                            pb: 0.5,
                            "&::-webkit-scrollbar": { display: "none" },
                        }}
                    >
                        <Stack direction="row" spacing={0.75}>
                            {getMyTaskDetails.map((item, idx) => {
                                const c = statusColor(item.status_name);
                                return (
                                <Button
                                    key={idx}
                                    onClick={() =>
                                        setState((prev) => ({ ...prev, activeIndex: idx }))
                                    }
                                    sx={getButtonStyles(idx, c)}
                                >
                                    <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: c, flexShrink: 0 }} />
                                    {item.status_name}
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: 0.25,
                                            fontSize: 12,
                                            fontWeight: 800,
                                            color: activeIndex === idx ? c : "#94a3b8",
                                        }}
                                    >
                                        {item.status_count}
                                    </Box>
                                </Button>
                                );
                            })}
                        </Stack>
                    </Box>
                </Box>

                <DataTableWithCheckbox
                    columns={columns}
                    data={filteredLeads}
                    renderRowActions={renderRowActions}
                    renderFilters={renderFilters()}
                    enableRowSelection={false}
                    status={status}
                    searchFirst
                    emptyMessage={getMyTaskDetails[activeIndex]?.status_name ? `No leads in "${getMyTaskDetails[activeIndex].status_name}".` : "No leads found."}
                />
            </Tabs>

            <TaskUpdateModal
                show={openUpdateModal}
                handleClose={() =>
                    setState(prev => ({
                        ...prev,
                        openUpdateModal: false
                    }))
                }
                rowData={selectedRowData}
                type="myTask"
            />

            <SnackbarMessage open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />

            <LeadWaChatConvo open={openLeadChatConvo} handleClose={() => dispatch(setOpenLeadChatConvo(false))} />

            <ContactProfileTabModal
                show={state.openContactEditModal}
                handleClose={() => setState(prev => ({ ...prev, openContactEditModal: false }))}
                rowData={state.selectedRowData}
                type="myTask"
            />
        </Box>
    );
}
